import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl, API_CONFIG } from '../config/api';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  UserPlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ServerIcon
} from '@heroicons/react/24/outline';

interface DatabaseInstance {
  id: string;
  name: string;
  host: string;
  port: number;
  database: string;
  username: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface UserData {
  username: string;
  password?: string;
  databases: string[];
  privileges: string[];
  expire_days: number;
}

const DatabaseInstances: React.FC = () => {
  const [instances, setInstances] = useState<DatabaseInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState<DatabaseInstance | null>(null);
  const [connectionTest, setConnectionTest] = useState<{[key: string]: any}>({});

  const [newInstance, setNewInstance] = useState({
    name: '',
    host: '',
    port: 5432,
    database: '',
    username: '',
    password: '',
    is_active: true
  });

  const [newUser, setNewUser] = useState<UserData>({
    username: '',
    password: '',
    databases: [],
    privileges: ['SELECT', 'INSERT', 'UPDATE', 'DELETE'],
    expire_days: 30
  });

  const availablePrivileges = [
    'SELECT', 'INSERT', 'UPDATE', 'DELETE', 'ALL'
  ];

  useEffect(() => {
    fetchInstances();
  }, []);

  const fetchInstances = async () => {
    try {
      setLoading(true);
      const response = await axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.DATABASE_INSTANCES));
      setInstances(response.data.data?.instances || []);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to fetch instances');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (instance: DatabaseInstance) => {
    try {
      const response = await axios.get(buildApiUrl(`${API_CONFIG.ENDPOINTS.DATABASE_INSTANCES}/${instance.id}/test-connection`));
      setConnectionTest(prev => ({
        ...prev,
        [instance.id]: {
          success: response.data.success,
          ...response.data.data
        }
      }));
    } catch (error: any) {
      setConnectionTest(prev => ({
        ...prev,
        [instance.id]: { success: false, error: error.response?.data?.error || 'Connection failed' }
      }));
    }
  };

  const addInstance = async () => {
    try {
      await axios.post(buildApiUrl(API_CONFIG.ENDPOINTS.DATABASE_INSTANCES), newInstance);
      setShowAddModal(false);
      setNewInstance({
        name: '',
        host: '',
        port: 5432,
        database: '',
        username: '',
        password: '',
        is_active: true
      });
      fetchInstances();
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to add instance');
    }
  };

  const removeInstance = async (instanceId: string) => {
    if (!window.confirm('Are you sure you want to remove this instance?')) return;
    
    try {
      await axios.delete(buildApiUrl(`${API_CONFIG.ENDPOINTS.DATABASE_INSTANCES}/${instanceId}`));
      fetchInstances();
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to remove instance');
    }
  };

  const createUser = async () => {
    if (!selectedInstance) return;
    
    try {
      const response = await axios.post(buildApiUrl(`${API_CONFIG.ENDPOINTS.DATABASE_INSTANCES}/${selectedInstance.id}/users`), newUser);
      setShowUserModal(false);
      setNewUser({
        username: '',
        password: '',
        databases: [],
        privileges: ['SELECT', 'INSERT', 'UPDATE', 'DELETE'],
        expire_days: 30
      });
      alert(`User created successfully!\nUsername: ${response.data.username}\nPassword: ${response.data.password}`);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const getStatusIcon = (instance: DatabaseInstance) => {
    if (instance.is_active) {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    }
    return <XCircleIcon className="h-5 w-5 text-red-500" />;
  };

  const getStatusText = (instance: DatabaseInstance) => {
    if (instance.is_active) return 'Active';
    return 'Inactive';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Database Instances</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage PostgreSQL database instances for monitoring
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Instance
        </button>
      </div>

      {error && (
        <div className="alert-danger">
          <p>{error}</p>
        </div>
      )}

      {/* Instances Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
        {instances.map((instance) => (
          <div key={instance.id} className="bg-white rounded-lg shadow border">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <ServerIcon className="h-6 w-6 text-gray-400 mr-3" />
                  <h3 className="text-lg font-medium text-gray-900">{instance.name}</h3>
                </div>
                {getStatusIcon(instance)}
              </div>
              
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Host</span>
                  <span className="text-sm font-medium">{instance.host}:{instance.port}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Database</span>
                  <span className="text-sm font-medium">{instance.database}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">User</span>
                  <span className="text-sm font-medium">{instance.username}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Status</span>
                  <span className={`text-sm font-medium ${
                    instance.is_active ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {getStatusText(instance)}
                  </span>
                </div>
              </div>

              {/* Connection Test Result */}
              {connectionTest[instance.id] && (
                <div className="mb-4 p-3 rounded-md bg-gray-50">
                  {connectionTest[instance.id].success ? (
                    <div className="text-sm">
                      <div className="flex items-center text-green-600 mb-2">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Connection successful
                      </div>
                      <div className="text-xs text-gray-600">
                        <div>Version: {connectionTest[instance.id].version}</div>
                        <div>Connections: {connectionTest[instance.id].connections}</div>
                        <div>Databases: {connectionTest[instance.id].databases}</div>
                        <div>Users: {connectionTest[instance.id].users}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-red-600">
                      <div className="flex items-center">
                        <XCircleIcon className="h-4 w-4 mr-1" />
                        Connection failed
                      </div>
                      <div className="text-xs mt-1">{connectionTest[instance.id].error}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-2">
                <button
                  onClick={() => testConnection(instance)}
                  className="btn-secondary flex-1 flex items-center justify-center text-sm"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  Test
                </button>
                
                <button
                  onClick={() => {
                    setSelectedInstance(instance);
                    setShowUserModal(true);
                  }}
                  className="btn-primary flex-1 flex items-center justify-center text-sm"
                >
                  <UserPlusIcon className="h-4 w-4 mr-1" />
                  Add User
                </button>
                
                <button
                  onClick={() => removeInstance(instance.id)}
                  className="btn-danger flex items-center justify-center text-sm"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Instance Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add Database Instance</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instance Name
                </label>
                <input
                  type="text"
                  value={newInstance.name}
                  onChange={(e) => setNewInstance({...newInstance, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="My PostgreSQL Instance"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Host
                  </label>
                  <input
                    type="text"
                    value={newInstance.host}
                    onChange={(e) => setNewInstance({...newInstance, host: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="localhost"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Port
                  </label>
                  <input
                    type="number"
                    value={newInstance.port}
                    onChange={(e) => setNewInstance({...newInstance, port: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="5432"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Database
                </label>
                <input
                  type="text"
                  value={newInstance.database}
                  onChange={(e) => setNewInstance({...newInstance, database: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="postgres"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    value={newInstance.username}
                    onChange={(e) => setNewInstance({...newInstance, username: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="postgres"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={newInstance.password}
                    onChange={(e) => setNewInstance({...newInstance, password: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="password"
                  />
                </div>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={newInstance.is_active}
                  onChange={(e) => setNewInstance({...newInstance, is_active: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                  Enable monitoring (uncheck to exclude from PostgreSQL and Database Users tabs)
                </label>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={addInstance}
                className="btn-primary flex-1"
              >
                Add Instance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showUserModal && selectedInstance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Create Database User</h2>
            <p className="text-sm text-gray-600 mb-4">Instance: {selectedInstance.name}</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="newuser"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password (leave empty for auto-generate)
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Auto-generate"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Databases (comma-separated)
                </label>
                <input
                  type="text"
                  value={newUser.databases.join(', ')}
                  onChange={(e) => setNewUser({...newUser, databases: e.target.value.split(',').map(s => s.trim())})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="database1, database2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Privileges
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {availablePrivileges.map((privilege) => (
                    <label key={privilege} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newUser.privileges.includes(privilege)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewUser({...newUser, privileges: [...newUser.privileges, privilege]});
                          } else {
                            setNewUser({...newUser, privileges: newUser.privileges.filter(p => p !== privilege)});
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{privilege}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expire Days
                </label>
                <input
                  type="number"
                  value={newUser.expire_days}
                  onChange={(e) => setNewUser({...newUser, expire_days: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  min="1"
                  max="365"
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowUserModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={createUser}
                className="btn-primary flex-1"
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseInstances;
